import { primitives } from "./primitives";
import type { ThemeContract } from "./contract";

const a = primitives.color.actinic;
const night = primitives.color.night;
const tide = primitives.color.tide;

/**
 * "Actinic Glow": the brand gradient runs through the fluorescence colours —
 * green, sky, magenta — instead of a generic rainbow.
 */
const brandGradient = `linear-gradient(135deg, ${a.green} 0%, ${a.sky} 52%, ${a.magenta} 100%)`;
/** Light theme: the same run in the deep shades, so text.inverse (white) reads on it. */
const brandGradientLight = `linear-gradient(135deg, ${a.greenDeep} 0%, ${a.skyDeep} 52%, ${a.magentaDeep} 100%)`;

/**
 * Light theme of "Actinic Glow": a cool, faintly blue daylight ground; the
 * fluorescence hues in their deep shades so text and controls keep ≥ 4.5:1 /
 * ≥ 3:1 (tokens/__tests__/contrast.test.ts).
 */
export const reefGuideLightTheme: ThemeContract = {
  name: "reefGuideLight",
  color: {
    primary: a.greenDeep,
    primaryHover: a.greenDeeper,
    primarySubtle: "#e0f5ec",
    primarySubtleHover: "#cdeedf",
    secondary: a.magentaDeep,
    accent: a.goldDeep,
    background: {
      page: tide[50],
      surface: tide[0],
      surfaceAlt: tide[100],
      elevated: tide[0],
      modal: tide[0],
      hover: tide[150],
      selected: "#e0f5ec",
      overlay: "rgba(6, 11, 24, 0.45)",
      media: primitives.color.neutral[1000],
    },
    text: {
      primary: tide[900],
      secondary: tide[700],
      muted: tide[600],
      disabled: tide[400],
      inverse: tide[0],
    },
    border: {
      primary: tide[200],
      secondary: tide[250],
      control: tide[500],
      focus: a.greenDeep,
    },
    status: {
      success: "#0e7a4b",
      warning: a.goldDeep,
      danger: a.coralDeep,
      info: a.skyDeep,
      ai: a.violetDeep,
      excellent: "#0e7a4b",
      stable: a.skyDeep,
      critical: a.coralDeep,
      successSubtle: "#e3f6ec",
      warningSubtle: "#fff3dc",
      dangerSubtle: "#ffe8eb",
      infoSubtle: "#e5eefb",
    },
    // Data-series colours (trend lines, legend swatches): ≥ 3:1 on every background.
    chart: {
      kh: a.skyDeep,
      no3: "#0f8a75",
      po4: "#c2410c",
      ca: a.violetDeep,
      mg: a.magentaDeep,
      series6: a.coralDeep,
      series7: a.goldDeep,
      series8: "#4d7c0f",
    },
    gradient: {
      brand: brandGradientLight,
      ai: `linear-gradient(135deg, #f1eaff 0%, #e5eefb 100%)`,
    },
  },
  spacing: primitives.spacing,
  font: {
    family: primitives.fontFamily,
    size: primitives.fontSize,
    weight: primitives.fontWeight,
    lineHeight: primitives.lineHeight,
  },
  radius: primitives.radius,
  shadow: {
    none: primitives.shadow.none,
    sm: primitives.shadow.sm,
    md: primitives.shadow.md,
    lg: primitives.shadow.lg,
    focus: primitives.shadow.focusLight,
    glow: primitives.shadow.glowLight,
  },
  breakpoint: primitives.breakpoint,
  zIndex: primitives.zIndex,
};

/**
 * Dark theme of "Actinic Glow" (the default): the blue-black of a tank at
 * night, fluorescent green as the brand colour, magenta and gold as accents.
 */
export const reefGuideDarkTheme: ThemeContract = {
  name: "reefGuideDark",
  color: {
    primary: a.green,
    // Hover lightens; the dark label (text.inverse) keeps ≥ 4.5:1 on it.
    primaryHover: a.greenSoft,
    // 14 % / 22 % of the green pre-composited over background.surface.
    primarySubtle: "#143539",
    primarySubtleHover: "#1a4844",
    secondary: a.magenta,
    accent: a.gold,
    background: {
      page: night[900],
      surface: night[850],
      surfaceAlt: night[800],
      elevated: night[750],
      modal: night[700],
      hover: "#112038",
      selected: "rgba(77, 255, 176, 0.12)",
      overlay: "rgba(2, 6, 15, 0.75)",
      media: primitives.color.neutral[1000],
    },
    text: {
      primary: night[200],
      secondary: night[300],
      muted: night[400],
      disabled: "#56657f",
      inverse: "#04130c",
    },
    border: {
      primary: night[650],
      secondary: night[600],
      control: night[500],
      focus: a.green,
    },
    status: {
      success: a.green,
      warning: a.gold,
      danger: a.coral,
      info: a.sky,
      ai: a.violet,
      excellent: a.green,
      stable: a.sky,
      critical: a.coral,
      // Opaque: 14 % of the status colour pre-composited over background.surface.
      successSubtle: "#143539",
      warningSubtle: "#2d2d2b",
      dangerSubtle: "#2d2032",
      infoSubtle: "#192d44",
    },
    chart: {
      kh: a.sky,
      no3: a.green,
      po4: a.gold,
      ca: a.violet,
      mg: a.magenta,
      series6: a.coral,
      series7: primitives.color.reef.orange,
      series8: "#a3e635",
    },
    gradient: {
      brand: brandGradient,
      ai: `linear-gradient(135deg, #1a1f45 0%, #13284a 100%)`,
    },
  },
  spacing: primitives.spacing,
  font: {
    family: primitives.fontFamily,
    size: primitives.fontSize,
    weight: primitives.fontWeight,
    lineHeight: primitives.lineHeight,
  },
  radius: primitives.radius,
  shadow: {
    none: primitives.shadow.none,
    sm: primitives.shadow.darkSm,
    md: primitives.shadow.darkMd,
    lg: primitives.shadow.darkLg,
    focus: primitives.shadow.focusDark,
    glow: primitives.shadow.glowDark,
  },
  breakpoint: primitives.breakpoint,
  zIndex: primitives.zIndex,
};

export const defaultTheme = reefGuideDarkTheme;
